/** The studio footer's behavior half: reads the studio and keeps only what it has. Draws nothing. */
import { useMemo } from 'react';

import { useWebSession } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useMessagesPanel } from '../../messages/panelContext';
import { usePageMover } from '../../pageMover';
import {
  contract,
  type FooterAccountLink,
  type FooterLink,
  type StudioFooterActions,
  type StudioFooterEvents,
  type StudioFooterState,
} from './contract';

export type StudioFooterRuntime = PieceRuntime<typeof contract, StudioFooterState, StudioFooterActions>;

export function useStudioFooter(input: PieceInput<StudioFooterEvents> = {}): StudioFooterRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const query = useSiteStudio();
  const studio = query.data;
  const signedIn = useWebSession().status === 'signedIn';
  const { openMessages } = useMessagesPanel();

  const links = useMemo<FooterLink[]>(() => {
    if (!studio) return [];
    const out: FooterLink[] = [];
    if (studio.hasServices) out.push({ id: 'services', label: 'Services', href: '/#services' });
    if (studio.hasEvents) out.push({ id: 'events', label: 'Events', href: '/events' });
    return out;
  }, [studio]);

  const account = useMemo<FooterAccountLink[]>(
    () =>
      signedIn
        ? [
            { id: 'bookings', label: 'Bookings', href: '/account/bookings' },
            { id: 'messages', label: 'Messages', href: null },
            { id: 'profile', label: 'Profile', href: '/account/profile' },
          ]
        : [],
    [signedIn],
  );

  const location = studio?.location?.trim() || null;
  const place = [location, studio?.establishedYear ? `Est. ${studio.establishedYear}` : null].filter(Boolean).join(' · ');
  const blurb = [studio?.tagline?.trim(), place].filter(Boolean).join(' ') || null;
  const status: StudioFooterState['status'] = studio ? 'ready' : query.isError ? 'error' : 'loading';

  const actions = useMemo<StudioFooterActions>(
    () => ({
      goHome: () => {
        emit('navigated', { linkId: 'home' });
        navigate('/');
      },
      open: (linkId) => {
        const link = links.find((l) => l.id === linkId);
        if (!link) return;
        emit('navigated', { linkId });
        navigate(link.href);
      },
      openAccount: (linkId) => {
        const link = account.find((l) => l.id === linkId);
        if (!link) return;
        emit('navigated', { linkId });
        if (link.href) navigate(link.href);
        else openMessages();
      },
    }),
    [links, account, emit, navigate, openMessages],
  );

  return {
    state: {
      status,
      name: studio?.name ?? '',
      logoUrl: studio?.logoUrl ?? null,
      blurb,
      location,
      links,
      year: new Date().getFullYear(),
      account,
    },
    actions,
    options,
  };
}
